import React, { useState, useEffect, useRef } from "react";
import {
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import {
  AddressBook,
  convertAddressBookToCreate,
  convertAddressBookToUpdate,
  normalize,
  validate,
} from "../../../Models/AddressBook";
import { AddressBookService as ShipmentAddressBookService } from "../../../services/shipment/addressBook.ts";
import { AddressBookService as RegistryAddressBookService } from "../../../services/registry/addressBook.ts";
import { AddressBookService as LogisticAddressBookService } from "../../../services/logistic/addressBook.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig";
import { useAppContext } from "../../../AppProvider";
import { useRegistryAddressBook } from "../../../hooks/useRegistryAddressBook";
import { AddressBookHandlerContext } from "./";
import { AddressBookHandlerProviderProps } from "./";

/* ===========================================================================
 * SERVICE REGISTRY
 * Maps provider service type to the correct API service implementation.
 * =========================================================================== */
const services = {
  logistic: { addressBookService: new LogisticAddressBookService() },
  registry: { addressBookService: new RegistryAddressBookService() },
  shipment: { addressBookService: new ShipmentAddressBookService() },
};

/* ===========================================================================
 * PROVIDER: AddressBookHandlerProvider
 * =========================================================================== */

/**
 * @component AddressBookHandlerProvider
 *
 * Centralizes the state management and business logic for CRUD operations
 * on AddressBook entities. Supports:
 *
 * - Loading an existing AddressBook (`getAddressBook`)
 * - Creating or updating an AddressBook (`saveAddressBook`)
 * - Specialized save logic for the registry service (`saveRegistryAddressBook`)
 * - Autosave mode for real-time updates
 * - Modal configuration handling
 * - Billing AddressBook comparison logic
 * - Error forwarding and external error synchronization
 *
 * The provider abstracts differences between multiple backend services
 * (`registry`, `logistic`, `shipment`) and exposes a unified API to consumers.
 *
 * @param {AddressBookHandlerProviderProps} props - Provider configuration.
 * @returns {JSX.Element} A React Context Provider for AddressBook operations.
 */
const AddressBookHandlerProvider: React.FC<AddressBookHandlerProviderProps> = ({
  id,
  children,
  action,
  ownerId,
  autosave = false,
  preLoader = false,
  label,
  callback,
  service = "registry",
  defaultValue = null,
  billing = { enabled: true },
  registry = { saveEnabled: false, searchEnabled: false },
  inError = false,
  searchInAddressBookParams,
  getErrors,
}) => {
  const addressBookService = services[service].addressBookService;

  const { dictionary, accessTokenPayload } = useAppContext();
  const { saveRegistryAddressBook: saveToRegistry } = useRegistryAddressBook();

  /**
   * Local AddressBook state.
   * Merges default "flags" with incoming defaults and ownerId.
   */
  const [data, setData] = useState<Partial<AddressBook>>({
    myAddress: false,
    gdo: false,
    ztl: false,
    priv: false,
    ownerId,
    ...defaultValue,
  });

  /** Indicates loading state for async operations. */
  const [loader, setLoader] = useState<boolean>(preLoader);

  /** Holds backend or validation errors. */
  const [error, setError] = useState<ErrorResponse<AddressBook> | null>(null);
  const prevError = useRef<ErrorResponse<AddressBook> | null>(null);

  /** Modal configuration state. */
  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig());

  /** Tracks if billing addressBook differs from current addressBook. */
  const [
    billingAddressBookIsDifferentFromTheAddressBook,
    setBillingAddressBookIsDifferentFromTheAddressBook,
  ] = useState<boolean>(false);

  /* ---------------------------------------------------------------------------
   * FUNCTION: getAddressBook
   * Fetch a specific AddressBook entity from backend.
   * ------------------------------------------------------------------------- */

  /**
   * Fetches and loads a single AddressBook entity by ID.
   *
   * @async
   * @param {string} id - The AddressBook ID to retrieve.
   * @returns {Promise<void>}
   */
  const getAddressBook = async (id: string): Promise<void> => {
    setLoader(true);
    try {
      const res = await addressBookService.get(id);

      // Attach client-only fields without overwriting backend fields
      res.registryId = data.registryId;
      res.location.registryId = data.location?.registryId;

      if (action === "clone") {
        const addressBook: Partial<AddressBook> = { ...res };
        delete addressBook.id;
        setData(addressBook);
        return;
      }

      setData(res);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: saveRegistryAddressBook
   * Handles registry-specific persistence logic.
   * ------------------------------------------------------------------------- */

  /**
   * Saves the AddressBook using the registry-specific logic
   * (may differ from normal create/edit flow).
   *
   * @async
   * @returns {Promise<void>}
   */
  const saveRegistryAddressBook = async (): Promise<void> => {
    setLoader(true);
    try {
      const updated = await saveToRegistry(data as AddressBook);
      if (updated) {
        setData(updated);
        callSuccessToast(dictionary.messages.save_successful);
      }
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: saveAddressBook
   * Validates and decides whether to create or update.
   * ------------------------------------------------------------------------- */

  /**
   * Validates and persists an AddressBook entity.
   * Automatically chooses between create() and edit() based on presence of an ID.
   *
   * @param {AddressBook} addressBook - The AddressBook model being modified.
   * @param {keyof AddressBook | null} property - Field whose validation error should be cleared.
   * @param {boolean} save - Whether to perform backend persistence.
   */
  const saveAddressBook = (
    addressBook: AddressBook,
    property: keyof AddressBook | null,
    save = true,
  ) => {
    const normalized = normalize(addressBook) as AddressBook;
    const cleaned = cleanObject(normalized) as AddressBook;

    if (property === "registryId") {
      setError(null);
    } else if (property) {
      removeError(property, error, prevError, setError);
    }

    setData(cleaned);

    if (!save) return;

    if (!autosave) setLoader(true);

    const validation = validate(cleaned);
    if (!validation.isValid) {
      if (!autosave || cleaned.id) {
        setError({ response: { data: validation.errors } });
      }
      setLoader(false);
      return;
    }

    cleaned.id ? edit(cleaned) : create(cleaned);
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: create
   * Creates a new AddressBook.
   * ------------------------------------------------------------------------- */

  /**
   * Creates a new AddressBook entity.
   *
   * @async
   * @param {AddressBook} addressBook
   * @returns {Promise<void>}
   */
  const create = async (addressBook: AddressBook) => {
    try {
      const payload = convertAddressBookToCreate(addressBook);
      const created = await addressBookService.create(payload);

      setData(created);
      callback?.(created);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * FUNCTION: edit
   * Updates an existing AddressBook.
   * ------------------------------------------------------------------------- */

  /**
   * Updates an existing AddressBook entity.
   *
   * @async
   * @param {AddressBook} addressBook
   * @returns {Promise<void>}
   */
  const edit = async (addressBook: AddressBook) => {
    try {
      const payload = convertAddressBookToUpdate(addressBook);
      await addressBookService.edit(payload);

      setData(addressBook);
      callback?.(addressBook);
    } catch (err) {
      setError(err as ErrorResponse<AddressBook>);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Keeps billing-addressBook comparison state updated.
   */
  useEffect(() => {
    if (data.id) {
      if (billing.addressBook?.id) {
        setBillingAddressBookIsDifferentFromTheAddressBook(
          data.id !== billing.addressBook.id,
        );
      }
    }
  }, [data.id, billing.addressBook?.id]);

  /**
   * Propagates errors externally when caller supplies an error callback.
   */
  useEffect(() => {
    if (getErrors) {
      getErrors(error);
    }
  }, [error]);

  /**
   * Forces validation display when the parent toggles `inError`.
   */
  useEffect(() => {
    if (inError) {
      const validation = validate(data as AddressBook);
      setError({ response: { data: validation.errors } });
    }
  }, [inError]);

  /**
   * Automatically fetches an AddressBook when ID changes.
   */
  useEffect(() => {
    if (id) getAddressBook(id);
  }, [id]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  return (
    <AddressBookHandlerContext.Provider
      value={{
        service,
        action,
        data,
        loader,
        error,
        modalConfig,
        registry,
        billing,
        autosave,
        label,
        ownerId: ownerId || accessTokenPayload.owner,
        billingAddressBookIsDifferentFromTheAddressBook,
        searchInAddressBookParams,
        callback,
        getAddressBook,
        saveAddressBook,
        saveRegistryAddressBook,
        setModalConfig,
        setBillingAddressBookIsDifferentFromTheAddressBook,
      }}
    >
      {children}
    </AddressBookHandlerContext.Provider>
  );
};

export { AddressBookHandlerProvider };
